/**
 * YouTube Login Page
 *
 * Allows users to sign into YouTube Music to enable authenticated streams.
 * Uses an iframe/popup approach similar to Android's WebView login.
 *
 * Ported from: LoginScreen.kt
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/common/PageHeader';
import { useAuthStore } from '@/stores/auth';

export function YouTubeLoginPage() {
  const navigate = useNavigate();
  const { setYouTubeCookies } = useAuthStore();
  const [step, setStep] = useState<'info' | 'login' | 'manual'>('info');
  const [manualCookies, setManualCookies] = useState('');

  const handleOpenYouTube = () => {
    // Open YouTube Music in a new tab
    window.open('https://music.youtube.com', '_blank');
    setStep('manual');
  };

  const handleSaveCookies = () => {
    if (manualCookies.trim()) {
      setYouTubeCookies(manualCookies.trim());
      navigate('/settings');
    }
  };

  return (
    <div className="min-h-full page-enter">
      <PageHeader title="YouTube Login" />

      <div className="px-4 pb-8 space-y-6">
        {step === 'info' && (
          <section className="card space-y-4">
            <h2 className="text-lg font-bold">Sign into YouTube Music</h2>
            <p className="text-on-surface-variant">
              Signing into your YouTube account enables access to your playlists,
              liked songs, and premium-quality streams (if you have a subscription).
            </p>
            <div className="space-y-3">
              <button
                className="btn-primary w-full"
                onClick={handleOpenYouTube}
              >
                Open YouTube Music
              </button>
              <p className="text-xs text-on-surface-variant text-center">
                After signing in, you'll need to copy your cookies
              </p>
            </div>
          </section>
        )}

        {step === 'manual' && (
          <section className="card space-y-4">
            <h2 className="text-lg font-bold">Import Cookies</h2>
            <p className="text-sm text-on-surface-variant">
              After signing into YouTube Music in the new tab:
            </p>
            <ol className="text-sm text-on-surface-variant list-decimal list-inside space-y-2">
              <li>Open browser DevTools (F12 or Cmd+Option+I)</li>
              <li>Go to Application tab &gt; Storage &gt; Cookies</li>
              <li>Select music.youtube.com</li>
              <li>Find and copy these values: SAPISID, __Secure-3PAPISID</li>
              <li>Format as: SAPISID=value; __Secure-3PAPISID=value</li>
            </ol>
            <textarea
              className="w-full h-32 p-3 bg-surface-variant rounded-lg text-sm font-mono"
              placeholder="SAPISID=xxx; __Secure-3PAPISID=xxx; ..."
              value={manualCookies}
              onChange={(e) => setManualCookies(e.target.value)}
            />
            <div className="flex gap-3">
              <button
                className="btn-secondary flex-1"
                onClick={() => setStep('info')}
              >
                Back
              </button>
              <button
                className="btn-primary flex-1"
                onClick={handleSaveCookies}
                disabled={!manualCookies.trim()}
              >
                Save Cookies
              </button>
            </div>
          </section>
        )}

        <section className="card">
          <h2 className="text-lg font-bold mb-4">Why Sign In?</h2>
          <ul className="space-y-2 text-sm text-on-surface-variant">
            <li>- Access your liked songs and playlists</li>
            <li>- Higher quality audio (if Premium subscriber)</li>
            <li>- Personalized recommendations</li>
          </ul>
        </section>

        <section className="card">
          <h2 className="text-lg font-bold mb-4">Privacy Note</h2>
          <p className="text-sm text-on-surface-variant">
            Your YouTube credentials are stored locally on your device only.
            They are never sent to our servers.
          </p>
        </section>
      </div>
    </div>
  );
}
